import { BskyAgent } from '@atproto/api';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { AuthSession, LoginCredentials } from '../types/auth';
import { Post, TimelineFeed } from '../types/post';

const SERVICE_URL = 'https://bsky.social';
const SESSION_STORAGE_KEY = '@pixelsky:session';

// Bluesky APIクライアントのシングルトンインスタンス
export const agent = new BskyAgent({
  service: SERVICE_URL,
});

/**
 * ログイン処理
 */
export const login = async (credentials: LoginCredentials): Promise<AuthSession> => {
  try {
    const response = await agent.login({
      identifier: credentials.identifier,
      password: credentials.password,
    });

    if (!response.success) {
      throw new Error('ログインに失敗しました');
    }

    const session: AuthSession = {
      accessJwt: response.data.accessJwt,
      refreshJwt: response.data.refreshJwt,
      handle: response.data.handle,
      did: response.data.did,
      email: response.data.email,
      active: true,
    };

    // セッションを永続化
    await saveSession(session);

    return session;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

/**
 * セッション復元
 */
export const resumeSession = async (): Promise<AuthSession | null> => {
  try {
    const sessionData = await AsyncStorage.getItem(SESSION_STORAGE_KEY);
    
    if (!sessionData) {
      return null;
    }

    const session: AuthSession = JSON.parse(sessionData);

    // セッションを復元（active: trueを設定）
    await agent.resumeSession({
      ...session,
      active: true,
    });

    return session;
  } catch (error) {
    console.error('Resume session error:', error);
    await clearSession();
    return null;
  }
};

/**
 * ログアウト
 */
export const logout = async (): Promise<void> => {
  try {
    await clearSession();
  } catch (error) {
    console.error('Logout error:', error);
    throw error;
  }
};

/**
 * セッション保存
 */
const saveSession = async (session: AuthSession): Promise<void> => {
  try {
    await AsyncStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch (error) {
    console.error('Save session error:', error);
    throw error;
  }
};

/**
 * セッションクリア
 */
const clearSession = async (): Promise<void> => {
  try {
    await AsyncStorage.removeItem(SESSION_STORAGE_KEY);
  } catch (error) {
    console.error('Clear session error:', error);
    throw error;
  }
};

/**
 * 現在のユーザープロフィールを取得
 */
export const getCurrentProfile = async () => {
  try {
    const response = await agent.getProfile({
      actor: agent.session?.did || '',
    });

    if (!response.success) {
      throw new Error('プロフィールの取得に失敗しました');
    }

    return response.data;
  } catch (error) {
    console.error('Get profile error:', error);
    throw error;
  }
};

/**
 * タイムラインを取得
 */
export const getTimeline = async (cursor?: string, limit: number = 50): Promise<TimelineFeed> => {
  try {
    const response = await agent.getTimeline({
      limit,
      cursor,
    });

    if (!response.success) {
      throw new Error('タイムラインの取得に失敗しました');
    }

    return {
      cursor: response.data.cursor,
      feed: response.data.feed.map(mapFeedItem).filter(Boolean) as Post[],
    };
  } catch (error) {
    console.error('Get timeline error:', error);
    throw error;
  }
};

/**
 * 発見（Discover）フィードを取得
 */
export const getDiscoverFeed = async (cursor?: string, limit: number = 30): Promise<TimelineFeed> => {
  try {
    // Bluesky Discovery feed URI
    const DISCOVER_FEED_URI = 'at://did:plc:z72i7hdynmk606gofwu7hpdi/app.bsky.feed.generator/whats-hot';
    
    const response = await agent.app.bsky.feed.getFeed({
      feed: DISCOVER_FEED_URI,
      limit,
      cursor,
    });

    if (!response.success) {
      throw new Error('発見フィードの取得に失敗しました');
    }

    return {
      cursor: response.data.cursor,
      feed: response.data.feed.map(mapFeedItem).filter(Boolean) as Post[],
    };
  } catch (error) {
    console.error('Get discover feed error:', error);
    throw error;
  }
};

/**
 * カスタムフィードを取得（動画が見つかるまで遡る）
 */
export const getCustomFeed = async (feedUri: string, cursor?: string, limit: number = 30): Promise<TimelineFeed> => {
  try {
    // リストかカスタムフィードかを判定
    const isList = feedUri.includes('app.bsky.graph.list');
    
    let currentCursor = cursor;
    let allFeed: Post[] = [];
    let attempts = 0;
    const maxAttempts = 2;

    while (attempts < maxAttempts) {
      let response;
      if (isList) {
        response = await agent.app.bsky.feed.getListFeed({
          list: feedUri,
          limit: 50,
          cursor: currentCursor,
        });
      } else {
        response = await agent.app.bsky.feed.getFeed({
          feed: feedUri,
          limit: 50,
          cursor: currentCursor,
        });
      }

      if (!response.success) break;

      const newPosts = response.data.feed.map(mapFeedItem).filter(Boolean) as Post[];
      allFeed = [...allFeed, ...newPosts];
      currentCursor = response.data.cursor;

      // YouTube風UIを意識し、動画を含む投稿を優先的に探す（ただし、1回目で十分な量があれば終了）
      const hasVideos = newPosts.some(item => {
        const embed = item.embed;
        if (!embed) return false;
        const type = embed.$type;
        return (
          type?.includes('video') || 
          type?.includes('recordWithMedia') ||
          (type?.includes('external') && item.embed.external?.uri?.match(/youtube\.com|youtu\.be/))
        );
      });

      // 既に投稿が十分にあるか、動画が見つかったか、次のページがない場合は終了
      if (allFeed.length >= limit || hasVideos || !currentCursor) break;
      attempts++;
    }

    return {
      cursor: currentCursor,
      feed: allFeed.slice(0, Math.max(limit, allFeed.length)),
    };
  } catch (error) {
    console.error('Get custom feed error:', error);
    throw error;
  }
};

/**
 * 保存済みフィード（ピン留め、保存）を取得
 */
export const getSavedFeeds = async (): Promise<{ pinned: any[], saved: any[] }> => {
  try {
    const preferences = await agent.app.bsky.actor.getPreferences();
    if (!preferences.success) {
      throw new Error('設定の取得に失敗しました');
    }

    // V1: savedFeedsPref, V2: savedFeedsPrefV2
    const savedFeedsPref = preferences.data.preferences.find(
      (pref: any) => pref.$type === 'app.bsky.actor.defs#savedFeedsPref'
    ) as any;
    
    const savedFeedsPrefV2 = preferences.data.preferences.find(
      (pref: any) => pref.$type === 'app.bsky.actor.defs#savedFeedsPrefV2'
    ) as any;

    let pinnedUris: string[] = [];
    let savedUris: string[] = [];

    if (savedFeedsPrefV2 && savedFeedsPrefV2.items) {
      // V2の処理
      pinnedUris = savedFeedsPrefV2.items
        .filter((item: any) => item.pinned)
        .map((item: any) => item.value);
      savedUris = savedFeedsPrefV2.items
        .map((item: any) => item.value);
    } else if (savedFeedsPref) {
      // V1の処理
      pinnedUris = Array.isArray(savedFeedsPref.pinned) ? savedFeedsPref.pinned : [];
      savedUris = Array.isArray(savedFeedsPref.saved) ? savedFeedsPref.saved : [];
    }

    if (pinnedUris.length === 0 && savedUris.length === 0) {
      return { 
        pinned: [{ uri: 'following', displayName: 'フォロー中' }], 
        saved: [{ uri: 'following', displayName: 'フォロー中' }] 
      };
    }

    const allUris = [...new Set([...pinnedUris, ...savedUris])];
    
    // タイムライン（Following）は'following'という特別な識別子、それ以外はat://...
    const atUris = allUris.filter(uri => typeof uri === 'string' && uri.startsWith('at://'));
    const feedUris = atUris.filter(uri => uri.includes('app.bsky.feed.generator'));
    const listUris = atUris.filter(uri => uri.includes('app.bsky.graph.list'));

    let feedGeneratorsData: any[] = [];
    if (feedUris.length > 0) {
      const chunkSize = 20;
      for (let i = 0; i < feedUris.length; i += chunkSize) {
        const chunk = feedUris.slice(i, i + chunkSize);
        const response = await agent.app.bsky.feed.getFeedGenerators({
          feeds: chunk,
        });
        if (response.success) {
          feedGeneratorsData = [...feedGeneratorsData, ...response.data.feeds];
        }
      }
    }

    let listsData: any[] = [];
    if (listUris.length > 0) {
      // リストの詳細を取得
      for (const uri of listUris) {
        try {
          const response = await agent.app.bsky.graph.getList({
            list: uri,
            limit: 1,
          });
          if (response.success) {
            listsData.push(response.data.list);
          }
        } catch (e) {
          console.error(`Failed to fetch list ${uri}:`, e);
        }
      }
    }

    const mapUriToFeed = (uri: string) => {
      if (uri === 'following' || uri.includes('app.bsky.feed.getTimeline')) {
        return { uri: 'following', displayName: 'フォロー中', type: 'timeline' };
      }
      
      const generator = feedGeneratorsData.find(f => f.uri === uri);
      if (generator) {
        return {
          uri: generator.uri,
          displayName: generator.displayName,
          avatar: generator.avatar,
          creator: generator.creator,
          type: 'feed',
        };
      }

      const list = listsData.find(l => l.uri === uri);
      if (list) {
        return {
          uri: list.uri,
          displayName: list.name,
          avatar: list.avatar,
          creator: list.creator,
          type: 'list',
        };
      }

      return null;
    };

    const pinned = pinnedUris
      .map(mapUriToFeed)
      .filter(Boolean);

    const saved = savedUris
      .map(mapUriToFeed)
      .filter(Boolean);

    // Followingがpinnedの先頭にない場合は追加する
    if (!pinned.find(f => f.uri === 'following')) {
      pinned.unshift({ uri: 'following', displayName: 'フォロー中' });
    }

    return { pinned, saved };
  } catch (error) {
    console.error('Get saved feeds error:', error);
    throw error;
  }
};

/**
 * フィードアイテムをPost型にマッピング
 */
const mapFeedItem = (item: any): Post | null => {
  if (!item.post || (item.post.$type !== 'app.bsky.feed.defs#postView' && !item.post.uri)) {
    return null;
  }

  return {
    uri: item.post.uri,
    cid: item.post.cid,
    author: {
      did: item.post.author.did,
      handle: item.post.author.handle,
      displayName: item.post.author.displayName,
      avatar: item.post.author.avatar,
    },
    record: {
      text: item.post.record?.text || '',
      createdAt: item.post.record?.createdAt || new Date().toISOString(),
      reply: item.post.record?.reply,
      facets: item.post.record?.facets,
      embed: item.post.record?.embed,
    },
    replyCount: item.post.replyCount || 0,
    repostCount: item.post.repostCount || 0,
    likeCount: item.post.likeCount || 0,
    indexedAt: item.post.indexedAt,
    viewer: item.post.viewer,
    embed: item.post.embed,
    reason: item.reason ? {
      $type: item.reason.$type,
      by: {
        did: item.reason.by.did,
        handle: item.reason.by.handle,
        displayName: item.reason.by.displayName,
        avatar: item.reason.by.avatar,
      },
      indexedAt: item.reason.indexedAt,
    } : undefined,
  };
};

/**
 * 投稿にいいね
 */
export const likePost = async (uri: string, cid: string): Promise<string> => {
  try {
    const response = await agent.like(uri, cid);
    return response.uri;
  } catch (error) {
    console.error('Like post error:', error);
    throw error;
  }
};

/**
 * いいねを取り消し
 */
export const unlikePost = async (likeUri: string): Promise<void> => {
  try {
    await agent.deleteLike(likeUri);
  } catch (error) {
    console.error('Unlike post error:', error);
    throw error;
  }
};

/**
 * リポスト
 */
export const repost = async (uri: string, cid: string): Promise<string> => {
  try {
    const response = await agent.repost(uri, cid);
    return response.uri;
  } catch (error) {
    console.error('Repost error:', error);
    throw error;
  }
};

/**
 * リポストを取り消し
 */
export const unrepost = async (repostUri: string): Promise<void> => {
  try {
    await agent.deleteRepost(repostUri);
  } catch (error) {
    console.error('Unrepost error:', error);
    throw error;
  }
};

/**
 * 投稿詳細とスレッドを取得
 */
export const getPostThread = async (uri: string): Promise<any> => {
  try {
    const response = await agent.getPostThread({
      uri,
      depth: 10, // スレッドの深さ
    });

    if (!response.success) {
      throw new Error('投稿の取得に失敗しました');
    }

    return response.data.thread;
  } catch (error) {
    console.error('Get post thread error:', error);
    throw error;
  }
};

/**
 * 投稿を作成
 */
export const createPost = async (text: string, facets?: any[]): Promise<any> => {
  try {
    const response = await agent.post({
      text,
      facets,
      createdAt: new Date().toISOString(),
    });

    if (!response) {
      throw new Error('投稿の作成に失敗しました');
    }

    return response;
  } catch (error) {
    console.error('Create post error:', error);
    throw error;
  }
};

/**
 * リプライを作成
 */
export const createReply = async (
  text: string,
  parentUri: string,
  parentCid: string,
  rootUri: string,
  rootCid: string,
  facets?: any[]
): Promise<any> => {
  try {
    const response = await agent.post({
      text,
      facets,
      reply: {
        parent: {
          uri: parentUri,
          cid: parentCid,
        },
        root: {
          uri: rootUri,
          cid: rootCid,
        },
      },
      createdAt: new Date().toISOString(),
    });

    if (!response) {
      throw new Error('リプライの作成に失敗しました');
    }

    return response;
  } catch (error) {
    console.error('Create reply error:', error);
    throw error;
  }
};

/**
 * 引用投稿を作成
 */
export const createQuotePost = async (
  text: string,
  quoteUri: string,
  quoteCid: string,
  facets?: any[]
): Promise<any> => {
  try {
    const response = await agent.post({
      text,
      facets,
      embed: {
        $type: 'app.bsky.embed.record',
        record: {
          uri: quoteUri,
          cid: quoteCid,
        },
      },
      createdAt: new Date().toISOString(),
    });

    if (!response) {
      throw new Error('引用投稿の作成に失敗しました');
    }

    return response;
  } catch (error) {
    console.error('Create quote post error:', error);
    throw error;
  }
};

/**
 * 投稿をブックマーク
 */
export const bookmarkPost = async (uri: string, cid: string): Promise<string> => {
  try {
    if (!agent.session) {
      throw new Error('認証されていません');
    }

    // ブックマークは「いいね」と同じATプロトコルのrecordを使用
    // 実際のBlueskyではブックマーク専用のcollectionを使う必要がある
    const response = await agent.com.atproto.repo.createRecord({
      repo: agent.session.did,
      collection: 'app.bsky.graph.bookmark',
      record: {
        subject: { uri, cid },
        createdAt: new Date().toISOString(),
      },
    });

    return response.data.uri;
  } catch (error) {
    console.error('Bookmark error:', error);
    throw error;
  }
};

/**
 * ブックマークを解除
 */
export const unbookmarkPost = async (bookmarkUri: string): Promise<void> => {
  try {
    if (!agent.session) {
      throw new Error('認証されていません');
    }

    const rkey = bookmarkUri.split('/').pop();
    if (!rkey) {
      throw new Error('無効なブックマークURI');
    }

    await agent.com.atproto.repo.deleteRecord({
      repo: agent.session.did,
      collection: 'app.bsky.graph.bookmark',
      rkey,
    });
  } catch (error) {
    console.error('Unbookmark error:', error);
    throw error;
  }
};

/**
 * ユーザーをブロック
 */
export const blockUser = async (did: string): Promise<string> => {
  try {
    if (!agent.session) {
      throw new Error('認証されていません');
    }

    const response = await agent.com.atproto.repo.createRecord({
      repo: agent.session.did,
      collection: 'app.bsky.graph.block',
      record: {
        subject: did,
        createdAt: new Date().toISOString(),
      },
    });

    return response.data.uri;
  } catch (error) {
    console.error('Block user error:', error);
    throw error;
  }
};

/**
 * ユーザーのブロックを解除
 */
export const unblockUser = async (blockUri: string): Promise<void> => {
  try {
    if (!agent.session) {
      throw new Error('認証されていません');
    }

    const rkey = blockUri.split('/').pop();
    if (!rkey) {
      throw new Error('無効なブロックURI');
    }

    await agent.com.atproto.repo.deleteRecord({
      repo: agent.session.did,
      collection: 'app.bsky.graph.block',
      rkey,
    });
  } catch (error) {
    console.error('Unblock user error:', error);
    throw error;
  }
};

/**
 * リストを作成
 */
export const createList = async (
  name: string,
  description?: string,
  purpose: 'app.bsky.graph.defs#curatelist' | 'app.bsky.graph.defs#modlist' = 'app.bsky.graph.defs#curatelist'
): Promise<{ uri: string; cid: string }> => {
  try {
    if (!agent.session) {
      throw new Error('認証されていません');
    }

    const response = await agent.com.atproto.repo.createRecord({
      repo: agent.session.did,
      collection: 'app.bsky.graph.list',
      record: {
        purpose,
        name,
        description: description || '',
        createdAt: new Date().toISOString(),
      },
    });

    return {
      uri: response.data.uri,
      cid: response.data.cid,
    };
  } catch (error) {
    console.error('Create list error:', error);
    throw error;
  }
};

/**
 * リストを削除
 */
export const deleteList = async (listUri: string): Promise<void> => {
  try {
    if (!agent.session) {
      throw new Error('認証されていません');
    }

    const rkey = listUri.split('/').pop();
    if (!rkey) {
      throw new Error('無効なリストURI');
    }

    await agent.com.atproto.repo.deleteRecord({
      repo: agent.session.did,
      collection: 'app.bsky.graph.list',
      rkey,
    });
  } catch (error) {
    console.error('Delete list error:', error);
    throw error;
  }
};

/**
 * リストにユーザーを追加
 */
export const addToList = async (listUri: string, subjectDid: string): Promise<string> => {
  try {
    if (!agent.session) {
      throw new Error('認証されていません');
    }

    const response = await agent.com.atproto.repo.createRecord({
      repo: agent.session.did,
      collection: 'app.bsky.graph.listitem',
      record: {
        subject: subjectDid,
        list: listUri,
        createdAt: new Date().toISOString(),
      },
    });

    return response.data.uri;
  } catch (error) {
    console.error('Add to list error:', error);
    throw error;
  }
};

/**
 * リストからユーザーを削除
 */
export const removeFromList = async (listItemUri: string): Promise<void> => {
  try {
    if (!agent.session) {
      throw new Error('認証されていません');
    }

    const rkey = listItemUri.split('/').pop();
    if (!rkey) {
      throw new Error('無効なリストアイテムURI');
    }

    await agent.com.atproto.repo.deleteRecord({
      repo: agent.session.did,
      collection: 'app.bsky.graph.listitem',
      rkey,
    });
  } catch (error) {
    console.error('Remove from list error:', error);
    throw error;
  }
};

/**
 * ユーザーのリスト一覧を取得
 */
export const getLists = async (actor: string): Promise<any[]> => {
  try {
    const response = await agent.app.bsky.graph.getLists({
      actor,
    });

    if (!response.success) {
      throw new Error('リストの取得に失敗しました');
    }

    return response.data.lists;
  } catch (error) {
    console.error('Get lists error:', error);
    throw error;
  }
};

/**
 * リストのフィードを取得
 */
export const getListFeed = async (list: string, cursor?: string, limit: number = 30): Promise<TimelineFeed> => {
  try {
    const response = await agent.app.bsky.feed.getListFeed({
      list,
      limit,
      cursor,
    });

    if (!response.success) {
      throw new Error('リストフィードの取得に失敗しました');
    }

    return {
      cursor: response.data.cursor,
      feed: response.data.feed.map(mapFeedItem) as Post[],
    };
  } catch (error) {
    console.error('Get list feed error:', error);
    throw error;
  }
};

/**
 * タイムラインから動画投稿を取得（見つかるまで遡る）
 */
export const getVideoPosts = async (cursor?: string, limit: number = 40): Promise<TimelineFeed> => {
  try {
    let currentCursor = cursor;
    let allFeed: Post[] = [];
    let attempts = 0;
    const maxAttempts = 3;

    while (attempts < maxAttempts) {
      const response = await agent.getTimeline({
        limit: 100, 
        cursor: currentCursor,
      });

      if (!response.success) break;

      const newPosts = response.data.feed.map(mapFeedItem).filter(Boolean) as Post[];
      allFeed = [...allFeed, ...newPosts];
      currentCursor = response.data.cursor;

      const hasVideos = newPosts.some(item => {
        const embed = item.embed;
        if (!embed) return false;
        const type = embed.$type;
        return (
          type?.includes('video') || 
          type?.includes('recordWithMedia') ||
          (type?.includes('external') && item.embed.external?.uri?.match(/youtube\.com|youtu\.be/))
        );
      });

      if (hasVideos || !currentCursor) break;
      attempts++;
    }

    return {
      cursor: currentCursor,
      feed: allFeed,
    };
  } catch (error) {
    console.error('Get video posts error:', error);
    throw error;
  }
};

/**
 * 特定のユーザーの動画投稿を取得（見つかるまで遡る）
 */
export const getAuthorVideoPosts = async (actor: string, cursor?: string, limit: number = 40): Promise<TimelineFeed> => {
  try {
    let currentCursor = cursor;
    let allFeed: Post[] = [];
    let attempts = 0;
    const maxAttempts = 3;

    while (attempts < maxAttempts) {
      const response = await agent.getAuthorFeed({
        actor,
        limit: 100,
        cursor: currentCursor,
      });

      if (!response.success) break;

      const newPosts = response.data.feed.map(mapFeedItem).filter(Boolean) as Post[];
      allFeed = [...allFeed, ...newPosts];
      currentCursor = response.data.cursor;

      const hasVideos = newPosts.some(item => {
        const embed = item.embed;
        if (!embed) return false;
        const type = embed.$type;
        return (
          type?.includes('video') || 
          type?.includes('recordWithMedia') ||
          (type?.includes('external') && item.embed.external?.uri?.match(/youtube\.com|youtu\.be/))
        );
      });

      if (hasVideos || !currentCursor) break;
      attempts++;
    }

    return {
      cursor: currentCursor,
      feed: allFeed,
    };
  } catch (error) {
    console.error('Get author video posts error:', error);
    throw error;
  }
};

/**
 * プロフィール情報を取得（Followステータスを含む）
 */
export const getProfile = async (actor: string): Promise<any> => {
  try {
    const response = await agent.getProfile({ actor });

    if (!response.success) {
      throw new Error('プロフィールの取得に失敗しました');
    }

    return response.data;
  } catch (error) {
    console.error('Get profile error:', error);
    throw error;
  }
};

/**
 * ユーザーをフォロー
 */
export const followUser = async (did: string): Promise<string> => {
  try {
    if (!agent.session) {
      throw new Error('認証されていません');
    }

    const response = await agent.follow(did);
    return response.uri;
  } catch (error) {
    console.error('Follow user error:', error);
    throw error;
  }
};

/**
 * ユーザーのフォローを解除
 */
export const unfollowUser = async (followUri: string): Promise<void> => {
  try {
    if (!agent.session) {
      throw new Error('認証されていません');
    }

    await agent.deleteFollow(followUri);
  } catch (error) {
    console.error('Unfollow user error:', error);
    throw error;
  }
};

/**
 * 検索から動画を含む投稿を取得
 */
export const searchVideoPosts = async (query: string = '', cursor?: string, limit: number = 40): Promise<TimelineFeed> => {
  try {
    // 検索APIを使用して動画を含む投稿を検索
    // 'video'という単語が含まれていなくても、メディアタイプを指定して検索を試みる
    // 注意: 現在のBluesky APIには「動画のみ」をフィルタリングする強力な公式検索パラメータはありませんが、
    // クエリを工夫することで精度を上げられます。
    const response = await agent.app.bsky.feed.searchPosts({
      q: query || 'youtube.com OR youtu.be OR bsky.video', // 動画に関係しそうなURLをクエリに含める
      limit: 100, // 最大まで取得
      cursor,
    });

    if (!response.success) {
      throw new Error('動画検索に失敗しました');
    }

    // `convertPostToVideoPost` でより高度な判定を行うため、ここでは基本的なフィルタリングのみ行う
    const videoPosts = response.data.posts
      .map(post => ({
        post,
        reply: undefined,
        reason: undefined,
      }))
      .map(mapFeedItem)
      .filter(Boolean) as Post[];

    return {
      cursor: response.data.cursor,
      feed: videoPosts,
    };
  } catch (error) {
    console.error('Search video posts error:', error);
    throw error;
  }
};
